import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { errorMessages } from '~/constants/errorMessages.ts';
import api from '~/libs/api';
import { TDictionaryAddress } from './types';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { AGGRESSOR_COUNTRY_CODES } from '~/constants/common';
import { useSelector } from 'react-redux';
import { RootState } from '~/store/store';

const getAddress = (token: string) => async () => {
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  try {
    const { data } = await api.get(`/dictionary-service/dictionary/address`, { headers });
    return data;
  } catch {
    throw Error(errorMessages.SERVICE_UNAVAILABLE);
  }
};

export const useAddressQuery = (regionId?: string) => {
  const token = useSelector((state: RootState) => state.users.token);
  const { data, error, isLoading, isError } = useQuery<TDictionaryAddress>({
    queryKey: ['address'],
    queryFn: getAddress(token),
    staleTime: 30 * 60 * 1000,
  });

  const countryOptions: TAutocompleteOption[] = useMemo(() => {
    if (!data?.countries) return [];
    return data?.countries.map((country) => ({
      value: country.k040,
      label: country.name,
    }));
  }, [data]);

  const regionOptions: TAutocompleteOption[] = useMemo(() => {
    if (!data?.regions) return [];
    return data?.regions.map((region) => ({
      value: region.id?.toString(),
      label: region.name,
    }));
  }, [data]);

  const districtOptions: TAutocompleteOption[] = useMemo(() => {
    if (!data?.districts) return [];
    const values = regionId
      ? data?.districts.filter((district) => district.regionId?.toString() === regionId)
      : data?.districts;

    return values.map((district) => ({
      value: district.id?.toString(),
      label: district.nameRaj,
    }));
  }, [data, regionId]);

  const nonAgressionCountryOptions: TAutocompleteOption[] = useMemo(() => {
    if (!countryOptions) return [];
    return countryOptions.filter(
      (country) => !Object.values(AGGRESSOR_COUNTRY_CODES).includes(country.value)
    );
  }, [countryOptions]);

  return {
    countries: data?.countries,
    districts: data?.districts,
    regions: data?.regions,
    countryOptions,
    regionOptions,
    districtOptions,
    nonAgressionCountryOptions,
    error,
    isLoading,
    isError,
  };
};
